import { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import { useBranchSelection } from "hooks/use-branch-selection";
import modules from "modules";

const initialCategoryRequestProductParams = {
  limit: 48,
  sort_direction: "asc",
  keyword: "",
};

const MAX_ITEMS_PER_CATEGORY = 10; // Limit for SwiperCarousel

const { actions: productsActions } = modules.products;

// Custom Hook for fetching categorized products
const useFetchCategoryItems = (manufacturer) => {
  const dispatch = useDispatch();
  const [productItems, setProductItems] = useState({});
  const [isProductLoading, setIsProductLoading] = useState({});
  const { selectedBranch } = useBranchSelection();
  const [facets, setFacets] = useState([]);

  // Fetch facets
  const fetchFacets = useCallback(async () => {
    setIsProductLoading((prev) => ({ ...prev, [manufacturer]: true }));

    try {
      const response = unwrapResult(
        await dispatch<any>(
          productsActions.getPoweredBuildBrandProductsThunk({
            ...initialCategoryRequestProductParams,
            qmanufacturer: manufacturer,
            branch_id: selectedBranch?.id,
            facets_only: 1,
          })
        )
      );

      if (response?.success) {
        const extractedFacets =
          response?.originalData?.facets?.[0]?.counts?.map(
            (filter) => filter.value
          ) || [];
        setFacets(extractedFacets);
      }
    } catch (error) {
      console.error(`Error fetching facets for ${manufacturer}:`, error);
    }

    setIsProductLoading((prev) => ({ ...prev, [manufacturer]: false }));
  }, [dispatch, manufacturer, selectedBranch?.id]);

  // Fetch all products across pages and limit each category to 10 items
  const getAllProductItems = useCallback(async () => {
    setIsProductLoading(true);
    let allProducts = [];

    try {
      // Fetch the first page to determine `last_page`
      const firstPageResponse = unwrapResult(
        await dispatch<any>(
          productsActions.getPoweredBuildBrandProductsThunk({
            ...initialCategoryRequestProductParams,
            qmanufacturer: manufacturer,
            branch_id: selectedBranch?.id,
          })
        )
      );

      if (!firstPageResponse?.success) {
        throw new Error("Failed to fetch first page");
      }

      allProducts = [...firstPageResponse.originalData?.data];

      // Extract last_page
      const lastPage = firstPageResponse.originalData?.meta?.last_page || 1;

      // Fetch remaining pages in parallel
      if (lastPage > 1) {
        const pageRequests = [];
        for (let page = 2; page <= lastPage; page++) {
          pageRequests.push(
            dispatch<any>(
              productsActions.getPoweredBuildBrandProductsThunk({
                ...initialCategoryRequestProductParams,
                qmanufacturer: manufacturer,
                branch_id: selectedBranch?.id,
                page,
              })
            ).then(unwrapResult)
          );
        }

        // Wait for all API calls to complete
        const remainingPageResponses = await Promise.all(pageRequests);

        // Combine all products from remaining pages
        remainingPageResponses.forEach((response) => {
          if (response?.success) {
            allProducts = [...allProducts, ...response.originalData?.data];
          }
        });
      }

      // Group products by category and limit each category to 10 items
      const groupedProducts = allProducts.reduce((acc, product) => {
        const category = product.category || "Uncategorized";
        if (!acc[category]) acc[category] = [];
        if (acc[category].length < MAX_ITEMS_PER_CATEGORY) {
          acc[category].push(product);
        }
        return acc;
      }, {});

      setProductItems(groupedProducts);
    } catch (error) {
      console.error(`Error fetching all products for ${manufacturer}:`, error);
    }

    setIsProductLoading(false);
  }, [dispatch, manufacturer, selectedBranch?.id]);

  // Fetch facets first
  useEffect(() => {
    if (selectedBranch) {
      fetchFacets();
    }
  }, [fetchFacets, selectedBranch]);

  // Fetch all products after facets are available
  useEffect(() => {
    if (facets.length > 0) {
      getAllProductItems();
    }
  }, [facets, getAllProductItems]);

  return { productItems, isProductLoading, facets };
};

export default useFetchCategoryItems;
